import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
    IconButton,
    Box,
    Button,
    Typography,
    TextField,
    Autocomplete,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    InputAdornment,
    Checkbox,
    Chip,
    FormControlLabel,
    Popper,
    TableSortLabel,
    Portal,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import DropzoneArea from '../DropzoneArea';
import MdbMissingAccountCardsTable from './MdbMissingAccountCardsTable';
import MissingVatReportRowsTable from './MissingVatReportRowsTable';
import useMissingVatReportRows from './useMissingVatReportRows';
import EnumsService from '../../../../Services/enums.service';
import Constants from '../../../../assests/Constants/constants';
import styles from './MdbImportModal.module.scss';

const BorderBoxButton = styled(Button)({
    boxSizing: 'border-box !important',
});

const PkudaSortCodeStyledPopper = styled(Popper)(({ theme }) => ({
    zIndex: 9999,
    '& .MuiPaper-root': {
        borderRadius: '8px',
        background: '#FFF',
        boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.15)',
        minWidth: '200px',
    },
    '& .MuiAutocomplete-listbox': {
        padding: theme.spacing(1),
        maxHeight: '280px',
    },
    '& .MuiAutocomplete-option': {
        borderRadius: '4px',
        padding: '8px 12px',
        fontSize: '0.875rem',
        margin: '2px 0',
        '&[aria-selected="true"]': {
            background: 'rgba(48, 79, 255, 0.12) !important',
            fontWeight: 600,
        },
        '&.Mui-focused': {
            background: 'rgba(249, 170, 42, 0.15) !important',
        },
        '&:hover': {
            background: 'rgba(249, 170, 42, 0.25) !important',
        },
    },
}));

const MdbImportModal = ({
    open,
    onClose,
    mode = 'journal',
    direction = 'rtl',
    t,
    client,
    token,
    user,
    previewMdbJournal,
    commitMdbJournal,
    importMdbAccountCards,
    createMissingSortCodes,
    formattedSortCodeVatType = [],
    formattedSortCodesGroupNamesOptions = [],
    accountCardOptions = [],
    setAccountCardOptions,
    sortCodeOptions = [],
    palCodes = [],
    fetchEnums,
    setLoading,
    onSuccess,
}) => {
    const [mdbFile, setMdbFile] = useState(null);
    const [mdbSortCodeFile, setMdbSortCodeFile] = useState(null);
    const [mdbVatReportFiles, setMdbVatReportFiles] = useState([]); // { id, file, yearMonth }[]
    const [mdbCardsDone, setMdbCardsDone] = useState(false);
    const [mdbMissingSortCodes, setMdbMissingSortCodes] = useState([]);
    const [mdbEditableMissing, setMdbEditableMissing] = useState([]);
    const [mdbOriginalMissingSortCodes, setMdbOriginalMissingSortCodes] = useState([]);
    const [mdbSelectedRows, setMdbSelectedRows] = useState([]);
    const [mdbBulkVatType, setMdbBulkVatType] = useState(null);
    const [mdbBulkGroup, setMdbBulkGroup] = useState(null);
    const [mdbBulkPar, setMdbBulkPar] = useState(null);
    const [mdbBulkVat, setMdbBulkVat] = useState(null);
    const [mdbPreviewDone, setMdbPreviewDone] = useState(false);
    const [mdbProcessing, setMdbProcessing] = useState(false);
    const [mdbMissingAccountCards, setMdbMissingAccountCards] = useState([]);
    const [mdbPreviewData, setMdbPreviewData] = useState(null);
    const [mdbPreviewSearch, setMdbPreviewSearch] = useState('');
    const [mdbPreviewPage, setMdbPreviewPage] = useState(0);
    const [mdbPreviewRowsPerPage, setMdbPreviewRowsPerPage] = useState(25);
    const [mdbSkipDuplicates, setMdbSkipDuplicates] = useState(true);
    const [mdbCardClassification, setMdbCardClassification] = useState(null);
    const [classificationPickerIds, setClassificationPickerIds] = useState([]);
    const [classificationAssignKind, setClassificationAssignKind] = useState(null);
    const [reportingMonthSource, setReportingMonthSource] = useState('Ref3');
    const [registrationMonthSource, setRegistrationMonthSource] = useState('DocumentDate');

    // Read through a ref so the hook always sees the current choice without re-creating callbacks.
    const registrationMonthSourceRef = useRef(registrationMonthSource);
    registrationMonthSourceRef.current = registrationMonthSource;

    const missingVatRows = useMissingVatReportRows({
        client,
        token,
        t,
        sortCodeOptions,
        getRegistrationMonthSource: useCallback(() => registrationMonthSourceRef.current, []),
    });

    const [documentDateFrom, setDocumentDateFrom] = useState('');
    const [documentDateTo, setDocumentDateTo] = useState('');
    const [reportingMonthFrom, setReportingMonthFrom] = useState('');
    const [reportingMonthTo, setReportingMonthTo] = useState('');
    const [orderBy, setOrderBy] = useState('lineNumber');
    const [order, setOrder] = useState('asc');
    const [filterSortCodeIds, setFilterSortCodeIds] = useState([]);
    const [filterDebitAccounts, setFilterDebitAccounts] = useState([]);
    const [filterCreditAccounts, setFilterCreditAccounts] = useState([]);
    const [filterStatus, setFilterStatus] = useState(null);
    const [previewFilterRules, setPreviewFilterRules] = useState([]); // { id, field, operator, value, value2 }
    const [previewFilterMatchAll, setPreviewFilterMatchAll] = useState(true);

    const reportingMonthSourceOptions = useMemo(() => [
        { value: 'Ref3', label: t('Ref3VatPeriod') || 'Ref3 (תקופת מע״מ)' },
        { value: 'ExtraDate1', label: t('ExtraDate1') || 'ExtraDate1' },
        { value: 'DatF3', label: t('DatF3') || 'DatF3' },
        { value: 'DocumentDate', label: t('DocumentDate') || 'Document Date' },
    ], [t]);

    const selectedReportingMonthSource = useMemo(
        () => reportingMonthSourceOptions.find(o => o.value === reportingMonthSource) || reportingMonthSourceOptions[0],
        [reportingMonthSourceOptions, reportingMonthSource]
    );

    const registrationMonthSourceOptions = useMemo(() => [
        { value: 'DocumentDate', label: t('DocumentDate') || 'Document Date' },
        { value: 'ReportingMonth', label: t('SameAsReportingMonth') || 'זהה לחודש הדיווח' },
    ], [t]);

    const selectedRegistrationMonthSource = useMemo(
        () => registrationMonthSourceOptions.find(o => o.value === registrationMonthSource) || registrationMonthSourceOptions[0],
        [registrationMonthSourceOptions, registrationMonthSource]
    );

    const guessVatYearMonthFromFileName = (fileName) => {
        const base = String(fileName || '').replace(/\.[^.]+$/, '');
        const match = base.match(/^(\d{1,2})/);
        if (!match) return '';
        const month = Number(match[1]);
        if (!month || month < 1 || month > 12) return '';
        const year = new Date().getFullYear();
        return `${year}-${String(month).padStart(2, '0')}`;
    };

    // VAT Excel filter/override only when files are attached; otherwise old MDB (+ optional sortcode) logic.
    const useVatReportLogic = mdbVatReportFiles.length > 0;

    const buildImportFilterOptions = () => ({
        reportingMonthSource,
        registrationMonthSource,
        documentDateFrom: documentDateFrom || undefined,
        documentDateTo: documentDateTo || undefined,
        reportingMonthFrom: reportingMonthFrom || undefined,
        reportingMonthTo: reportingMonthTo || undefined,
        // Omit entirely when empty so server uses old import path (no VAT filter / reporting override).
        ...(useVatReportLogic ? { vatReportFiles: mdbVatReportFiles } : {}),
        // Card lists that classify income / expense / equipment / other when TransType is empty.
        ...(mdbCardClassification ? { cardClassification: mdbCardClassification } : {}),
    });

    const emptyCardClassification = () => ({
        incomeCardIds: [],
        expenseCardIds: [],
        expenseEquipmentCardIds: [],
        otherCardIds: [],
    });

    const normalizeCardClassification = (raw) => {
        if (!raw) return null;
        return {
            incomeCardIds: [...(raw.incomeCardIds ?? raw.IncomeCardIds ?? [])],
            expenseCardIds: [...(raw.expenseCardIds ?? raw.ExpenseCardIds ?? [])],
            expenseEquipmentCardIds: [...(raw.expenseEquipmentCardIds ?? raw.ExpenseEquipmentCardIds ?? [])],
            otherCardIds: [...(raw.otherCardIds ?? raw.OtherCardIds ?? [])],
        };
    };

    const classificationKindKeys = {
        Income: 'incomeCardIds',
        Expense: 'expenseCardIds',
        ExpenseEquipment: 'expenseEquipmentCardIds',
        Other: 'otherCardIds',
    };

    const containsAnyKeyword = (haystack, needles) => {
        const text = String(haystack || '');
        if (!text) return false;
        return needles.some(n => n && text.includes(n));
    };

    /**
     * Strict name-only keywords (not card id / palcode).
     * Skip מס / תשומות / עסקאות.
     * Income ← הכנסות / מכירות / שכ״ט
     * Expense ← הוצאות only
     * Equipment ← ציוד / רכוש קבוע
     */
    const suggestKindFromCardName = (cardName) => {
        const name = String(cardName || '').trim();
        if (!name) return null;

        // Equipment first (e.g. "תשומות ציוד" / "ריהוט וציוד").
        if (containsAnyKeyword(name, ['ציוד', 'רכוש קבוע'])) {
            return 'ExpenseEquipment';
        }

        // Tax / VAT / inputs / transactions words are never income/expense auto-picks.
        if (containsAnyKeyword(name, ['מס', 'תשומות', 'עסקאות'])) {
            return null;
        }

        if (containsAnyKeyword(name, ['הכנסות', 'הכנסה', 'מכירות', 'מכירה', 'שכ"ט', 'שכ״ט', 'שב"ט', 'שב״ט'])) {
            return 'Income';
        }

        if (containsAnyKeyword(name, ['הוצאות', 'הוצאה'])) {
            return 'Expense';
        }

        return null;
    };

    /** Only cards that appear on preview transaction lines (debit/credit). */
    const collectPreviewTransactionCards = (entries = []) => {
        const map = new Map();
        const add = (id, name) => {
            const cardId = String(id || '').trim();
            if (!cardId) return;
            const cardName = String(name || '').trim();
            const existing = map.get(cardId);
            if (existing) {
                if (cardName && (!existing.name || cardName.length > existing.name.length)) {
                    existing.name = cardName;
                }
                return;
            }
            map.set(cardId, { cardId, name: cardName });
        };

        (entries || []).forEach(row => {
            add(row.debitAccount, row.debitAccountName);
            add(row.creditAccount, row.creditAccountName);
        });
        return map;
    };

    /**
     * Auto Classification from preview rows only — card name keywords.
     * Does not keep the server's case-wide PalCode lists (those flooded Income with hundreds of cards).
     */
    const buildAutoSuggestedClassificationFromPreview = (entries) => {
        const next = emptyCardClassification();
        collectPreviewTransactionCards(entries).forEach(({ cardId, name }) => {
            const kind = suggestKindFromCardName(name);
            if (!kind) return;
            const listKey = classificationKindKeys[kind];
            if (!(next[listKey] || []).some(id => String(id) === cardId)) {
                next[listKey] = [...(next[listKey] || []), cardId];
            }
        });
        return next;
    };

    const getCardKind = (classification, cardId) => {
        if (!classification || !cardId) return null;
        const id = String(cardId);
        if ((classification.incomeCardIds || []).some(x => String(x) === id)) return 'Income';
        if ((classification.expenseCardIds || []).some(x => String(x) === id)) return 'Expense';
        if ((classification.expenseEquipmentCardIds || []).some(x => String(x) === id)) return 'ExpenseEquipment';
        if ((classification.otherCardIds || []).some(x => String(x) === id)) return 'Other';
        return null;
    };

    /** Assign selected card ids to one kind; remove them from every other kind. */
    const setClassificationCardsForKind = (kind, cardIds) => {
        const key = classificationKindKeys[kind];
        if (!key) return;
        const selected = [...new Set((cardIds || []).map(id => String(id).trim()).filter(Boolean))];
        const selectedSet = new Set(selected);
        setMdbCardClassification(prev => {
            const base = normalizeCardClassification(prev) || emptyCardClassification();
            const next = emptyCardClassification();
            Object.entries(classificationKindKeys).forEach(([k, listKey]) => {
                if (k === kind) {
                    next[listKey] = selected;
                } else {
                    next[listKey] = (base[listKey] || []).filter(id => !selectedSet.has(String(id)));
                }
            });
            return next;
        });
    };

    const moveSelectedCardsToKind = (cardIds, kind) => {
        if (!kind || !cardIds?.length) return;
        const key = classificationKindKeys[kind];
        if (!key) return;
        const moving = [...new Set(cardIds.map(id => String(id).trim()).filter(Boolean))];
        const movingSet = new Set(moving);
        setMdbCardClassification(prev => {
            const base = normalizeCardClassification(prev) || emptyCardClassification();
            const next = emptyCardClassification();
            Object.entries(classificationKindKeys).forEach(([k, listKey]) => {
                const kept = (base[listKey] || []).filter(id => !movingSet.has(String(id)));
                next[listKey] = k === kind
                    ? [...new Set([...kept.map(String), ...moving])]
                    : kept.map(String);
            });
            return next;
        });
    };

    const classificationListsEqual = (a, b) => {
        const norm = (raw) => {
            const c = normalizeCardClassification(raw) || emptyCardClassification();
            return ['incomeCardIds', 'expenseCardIds', 'expenseEquipmentCardIds', 'otherCardIds']
                .map(k => [...(c[k] || [])].map(String).sort().join('\u0001'))
                .join('|');
        };
        return norm(a) === norm(b);
    };

    const applyPreviewResponse = (res, { skipAutoSuggest = false } = {}) => {
        const missing = res?.missingSortCodes ?? [];
        const missingCards = res?.missingAccountCards ?? [];
        // Ignore VAT-report extras unless VAT files were uploaded for this preview.
        const missingVatRowsFromResponse = useVatReportLogic ? (res?.missingVatReportRows ?? []) : [];
        setMdbMissingSortCodes(missing);
        setMdbMissingAccountCards(missingCards);
        missingVatRows.setRowsFromPreview(missingVatRowsFromResponse);

        const entries = res?.entries ?? [];
        const fromServer = normalizeCardClassification(res?.cardClassification) || emptyCardClassification();
        const classification = skipAutoSuggest
            ? fromServer
            : buildAutoSuggestedClassificationFromPreview(entries);

        setMdbCardClassification(classification);
        setMdbPreviewData({
            entries,
            totalLines: res?.parsedRows ?? 0,
            validCount: res?.validCount ?? 0,
            errorCount: res?.errorCount ?? 0,
            duplicateCount: res?.duplicateCount ?? 0,
            excludedByVatReportCount: useVatReportLogic ? (res?.excludedByVatReportCount ?? 0) : 0,
            errorMessages: useVatReportLogic
                ? (res?.errorMessages ?? [])
                : (res?.errorMessages ?? []).filter(msg => !String(msg).startsWith('ExcludedByVatReportSummary:')),
            cardClassification: classification,
        });
        setMdbPreviewPage(0);
        setMdbOriginalMissingSortCodes(missing.map(sc => ({
            sortCodeId: sc.sortCodeId,
            sortCodeName: sc.sortCodeName || ''
        })));
        setMdbEditableMissing(missing.map(sc => ({
            ...sc,
            sortCodeId: sc.sortCodeId || '',
            sortCodeName: sc.sortCodeName || '',
            sortCodeVatType: sc.sortCodeVatType ?? null,
            sortCodeGroup: sc.sortCodeGroup ?? null,
            participatesInProfitAndLoss: sc.participatesInProfitAndLoss ?? null,
            vat: sc.vat ?? null
        })));
        setMdbPreviewDone(true);

        return {
            classification,
            changed: !skipAutoSuggest && !classificationListsEqual(fromServer, classification),
        };
    };

    const resetState = () => {
        setMdbFile(null);
        setMdbSortCodeFile(null);
        setMdbVatReportFiles([]);
        setMdbCardsDone(false);
        setMdbPreviewDone(false);
        setMdbEditableMissing([]);
        setMdbMissingSortCodes([]);
        setMdbOriginalMissingSortCodes([]);
        setMdbMissingAccountCards([]);
        missingVatRows.reset();
        setMdbPreviewData(null);
        setMdbCardClassification(null);
        setClassificationPickerIds([]);
        setClassificationAssignKind(null);
        setMdbPreviewSearch('');
        setMdbPreviewPage(0);
        setMdbSelectedRows([]);
        setMdbBulkVatType(null);
        setMdbBulkGroup(null);
        setMdbBulkPar(null);
        setMdbBulkVat(null);
        setReportingMonthSource('Ref3');
        setPreviewFilterRules([]);
        setPreviewFilterMatchAll(true);
        setRegistrationMonthSource('DocumentDate');
        setDocumentDateFrom('');
        setDocumentDateTo('');
        setReportingMonthFrom('');
        setReportingMonthTo('');
        setOrderBy('lineNumber');
        setOrder('asc');
        setFilterSortCodeIds([]);
        setFilterDebitAccounts([]);
        setFilterCreditAccounts([]);
        setFilterStatus(null);
    };

    const handleClose = () => {
        resetState();
        onClose?.();
    };

    const handleMdbDrop = files => {
        if (files?.length) setMdbFile(files[0]);
        setMdbPreviewData(null);
        setMdbPreviewSearch('');
        setMdbPreviewDone(false);
        setDocumentDateFrom('');
        setDocumentDateTo('');
        setReportingMonthFrom('');
        setReportingMonthTo('');
        setFilterSortCodeIds([]);
        setFilterDebitAccounts([]);
        setFilterCreditAccounts([]);
        setFilterStatus(null);
        setOrderBy('lineNumber');
        setOrder('asc');
    };

    const handleMdbSortCodeDrop = files => {
        if (files?.length) setMdbSortCodeFile(files[0]);
    };

    const handleMdbVatReportDrop = files => {
        if (!files?.length) return;
        setMdbVatReportFiles(prev => {
            const byName = new Map(prev.map(item => [item.file.name, item]));
            files.forEach(f => {
                const existing = byName.get(f.name);
                byName.set(f.name, {
                    id: existing?.id || `${f.name}-${f.lastModified || Date.now()}`,
                    file: f,
                    yearMonth: existing?.yearMonth || guessVatYearMonthFromFileName(f.name),
                });
            });
            return Array.from(byName.values());
        });
        setMdbPreviewData(null);
        setMdbPreviewDone(false);
    };

    const clearVatReportPreviewState = () => {
        missingVatRows.reset();
        setMdbPreviewData(null);
        setMdbPreviewDone(false);
    };

    const removeMdbVatReportFile = (id) => {
        setMdbVatReportFiles(prev => prev.filter(item => item.id !== id));
        // Drop VAT-only UI; next preview without files uses classic logic.
        clearVatReportPreviewState();
    };

    const updateMdbVatReportYearMonth = (id, yearMonth) => {
        setMdbVatReportFiles(prev => prev.map(item =>
            item.id === id ? { ...item, yearMonth } : item
        ));
        setMdbPreviewData(null);
        setMdbPreviewDone(false);
    };

    const refreshAccountCardOptions = async () => {
        if (client && token) {
            const accountsResponse = await EnumsService.GetAccountCardsByCaseId(client.caseId, token);
            if (accountsResponse.ok) {
                const accountsData = await accountsResponse.json();
                const options = [{ name: t('AddAccountCard'), value1: 'AddItem' }, ...accountsData];
                setAccountCardOptions?.(options);
            }
        }
    };

    const handleMdbJournalPreview = async (sourceOverride, registrationSourceOverride, classificationOverride) => {
        if (!mdbFile) return;

        try {
            setMdbProcessing(true);
            const source = sourceOverride || reportingMonthSource;
            const sentExplicit = classificationOverride !== undefined;
            const classification = sentExplicit
                ? classificationOverride
                : mdbCardClassification;
            const res = await previewMdbJournal(mdbFile, mdbSortCodeFile, {
                reportingMonthSource: source,
                registrationMonthSource: registrationSourceOverride || registrationMonthSource,
                ...(mdbVatReportFiles.length > 0 ? { vatReportFiles: mdbVatReportFiles } : {}),
                ...(classification ? { cardClassification: classification } : {}),
            });
            // First load: auto-select cards from preview names (הכנסות→Income, …).
            // When the user sent an explicit Classification, keep it as-is.
            const applied = applyPreviewResponse(res, { skipAutoSuggest: sentExplicit });

            // Re-preview once with auto-selected lists so the Classification column matches the chips.
            if (!sentExplicit && applied?.changed && applied.classification) {
                const refreshed = await previewMdbJournal(mdbFile, mdbSortCodeFile, {
                    reportingMonthSource: source,
                    registrationMonthSource: registrationSourceOverride || registrationMonthSource,
                    ...(mdbVatReportFiles.length > 0 ? { vatReportFiles: mdbVatReportFiles } : {}),
                    cardClassification: applied.classification,
                });
                applyPreviewResponse(refreshed, { skipAutoSuggest: true });
            }

            try {
                await refreshAccountCardOptions();
            } catch (error) {
                console.error('Error fetching account cards:', error);
            }
        } catch (err) {
            console.error('MDB journal preview failed:', err);
            setMdbPreviewDone(false);
        } finally {
            setMdbProcessing(false);
        }
    };

    const handleReportingMonthSourceChange = async (newValue) => {
        const next = newValue?.value || 'Ref3';
        setReportingMonthSource(next);
        if (mdbFile && mdbPreviewDone) {
            await handleMdbJournalPreview(next);
        }
    };

    const handleRegistrationMonthSourceChange = async (newValue) => {
        const next = newValue?.value || 'DocumentDate';
        setRegistrationMonthSource(next);
        if (mdbFile && mdbPreviewDone) {
            await handleMdbJournalPreview(undefined, next);
        }
    };

    const handleMdbJournalCommit = async () => {
        if (!mdbFile) return;

        try {
            setMdbProcessing(true);

            let defaultSortCodeForEmpty = null;
            for (let i = 0; i < mdbEditableMissing.length; i++) {
                const current = mdbEditableMissing[i];
                const original = mdbOriginalMissingSortCodes[i];

                if ((!original || !original.sortCodeName || !original.sortCodeName.trim()) &&
                    current.sortCodeName && current.sortCodeName.trim()) {
                    defaultSortCodeForEmpty = current.sortCodeName.trim();
                    break;
                }
            }

            if (mdbEditableMissing && mdbEditableMissing.length > 0) {
                await createMissingSortCodes(mdbEditableMissing);
            }

            const res = await commitMdbJournal(
                mdbFile,
                mdbSortCodeFile,
                defaultSortCodeForEmpty,
                mdbSkipDuplicates,
                buildImportFilterOptions()
            );

            if (res && res.errorMessages && res.errorMessages.length > 0) {
                console.error('Import errors:', res.errorMessages);
                // Still close on success if some rows were imported (e.g. soft warnings only).
                const processedCount = res.processedCount ?? res[0] ?? 0;
                if (processedCount > 0) {
                    resetState();
                    onSuccess?.(res);
                    onClose?.();
                }
            } else {
                resetState();
                onSuccess?.(res);
                onClose?.();
            }
        } catch (err) {
            console.error('MDB journal commit error:', err);
        } finally {
            setMdbProcessing(false);
        }
    };

    const updateMdbMissing = (index, field, value) =>
        setMdbEditableMissing(prev => {
            const arr = [...prev];
            arr[index] = { ...arr[index], [field]: value };
            return arr;
        });

    const applyMdbBulk = () => {
        setMdbEditableMissing(prev =>
            prev.map(r =>
                mdbSelectedRows.includes(r.sortCodeId)
                    ? {
                        ...r,
                        sortCodeVatType: mdbBulkVatType?.value ?? r.sortCodeVatType,
                        sortCodeGroup: mdbBulkGroup?.value ?? r.sortCodeGroup,
                        participatesInProfitAndLoss: mdbBulkPar?.value ?? r.participatesInProfitAndLoss,
                        vat: mdbBulkVat?.value ?? r.vat,
                    }
                    : r
            )
        );
        setMdbSelectedRows([]);
        setMdbBulkVatType(null);
        setMdbBulkGroup(null);
        setMdbBulkPar(null);
        setMdbBulkVat(null);
    };

    const mdbMissingIncomplete = useMemo(
        () => mdbEditableMissing.some(sc =>
            !sc.sortCodeName ||
            !sc.sortCodeName.trim() ||
            !sc.sortCodeVatType ||
            sc.participatesInProfitAndLoss === null ||
            sc.participatesInProfitAndLoss === undefined ||
            sc.vat === null ||
            sc.vat === undefined
        ),
        [mdbEditableMissing]
    );

    const mdbPreviewEntries = mdbPreviewData?.entries ?? [];

    const classificationCardOptions = useMemo(() => {
        const map = new Map();
        const add = (id, name) => {
            const cardId = String(id || '').trim();
            if (!cardId) return;
            const existing = map.get(cardId);
            const cardName = String(name || '').trim();
            if (existing) {
                if (!existing.name && cardName) existing.name = cardName;
                return;
            }
            map.set(cardId, { cardId, name: cardName });
        };

        (accountCardOptions || []).forEach(o => {
            if (!o || o.value1 === 'AddItem') return;
            add(o.cardId ?? o.value1 ?? o.value, o.name ?? o.label);
        });
        (mdbMissingAccountCards || []).forEach(c => {
            add(c.cardId ?? c.CardId, c.cardName ?? c.CardName);
        });
        mdbPreviewEntries.forEach(row => {
            add(row.debitAccount, row.debitAccountName);
            add(row.creditAccount, row.creditAccountName);
        });
        const classification = normalizeCardClassification(mdbCardClassification);
        if (classification) {
            [
                ...classification.incomeCardIds,
                ...classification.expenseCardIds,
                ...classification.expenseEquipmentCardIds,
                ...classification.otherCardIds,
            ].forEach(id => add(id, ''));
        }

        return Array.from(map.values())
            .map(c => ({
                ...c,
                label: c.name ? `${c.cardId} - ${c.name}` : c.cardId,
                kind: getCardKind(classification, c.cardId),
            }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    }, [accountCardOptions, mdbMissingAccountCards, mdbPreviewEntries, mdbCardClassification]);

    const classificationKindOptions = useMemo(() => [
        { value: 'Income', label: t('Income') || 'הכנסה' },
        { value: 'Expense', label: t('Expense') || 'הוצאה' },
        { value: 'ExpenseEquipment', label: t('ExpenseEquipment') || 'הוצאת ציוד' },
        { value: 'Other', label: t('Other') || 'אחר' },
    ], [t]);

    const getClassificationOptionsForKind = (kind) => {
        const key = classificationKindKeys[kind];
        const ids = new Set((mdbCardClassification?.[key] || []).map(String));
        return classificationCardOptions.filter(o => ids.has(String(o.cardId)));
    };

    const handleApplyCardClassification = async () => {
        if (!mdbFile) return;
        const classification = normalizeCardClassification(mdbCardClassification) || emptyCardClassification();
        setMdbCardClassification(classification);
        await handleMdbJournalPreview(undefined, undefined, classification);
    };

    const handleAutoSuggestClassification = async () => {
        const merged = buildAutoSuggestedClassificationFromPreview(mdbPreviewEntries);
        setMdbCardClassification(merged);
        if (mdbFile) {
            await handleMdbJournalPreview(undefined, undefined, merged);
        }
    };

    const passesDateRange = (dateStr, from, to, byMonth = false) => {
        if (!from && !to) return true;
        if (!dateStr) return false;
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return false;
        if (byMonth) {
            const ym = d.getFullYear() * 12 + d.getMonth();
            if (from) {
                const f = new Date(from);
                if (!Number.isNaN(f.getTime()) && ym < f.getFullYear() * 12 + f.getMonth()) return false;
            }
            if (to) {
                const tDate = new Date(to);
                if (!Number.isNaN(tDate.getTime()) && ym > tDate.getFullYear() * 12 + tDate.getMonth()) return false;
            }
            return true;
        }
        const day = dateStr.slice(0, 10);
        if (from && day < from) return false;
        if (to && day > to) return false;
        return true;
    };

    const mdbDateFilteredEntries = useMemo(() => {
        return mdbPreviewEntries.filter(row =>
            passesDateRange(row.documentDate, documentDateFrom, documentDateTo, false) &&
            passesDateRange(row.reportingMonth, reportingMonthFrom, reportingMonthTo, true)
        );
    }, [mdbPreviewEntries, documentDateFrom, documentDateTo, reportingMonthFrom, reportingMonthTo]);

    const sortCodeFilterOptions = useMemo(() => {
        const map = new Map();
        mdbDateFilteredEntries.forEach(row => {
            const id = row.sortCodeId || '';
            const name = row.sortCodeName || '';
            const key = id || name;
            if (!key || map.has(key)) return;
            map.set(key, {
                value: key,
                label: id && name ? `${id} - ${name}` : (id || name),
                sortCodeId: id,
                sortCodeName: name,
            });
        });
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    }, [mdbDateFilteredEntries]);

    const debitAccountOptions = useMemo(() => {
        const set = new Set();
        mdbDateFilteredEntries.forEach(row => {
            if (row.debitAccount) set.add(row.debitAccount);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    }, [mdbDateFilteredEntries]);

    const creditAccountOptions = useMemo(() => {
        const set = new Set();
        mdbDateFilteredEntries.forEach(row => {
            if (row.creditAccount) set.add(row.creditAccount);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    }, [mdbDateFilteredEntries]);

    const statusFilterOptions = useMemo(() => [
        { value: 'valid', label: t('Valid') || 'Valid' },
        { value: 'duplicate', label: t('Duplicate') || 'Duplicate' },
        { value: 'error', label: t('Errors') || 'Error' },
    ], [t]);

    const mdbColumnFilteredEntries = useMemo(() => {
        return mdbDateFilteredEntries.filter(row => {
            if (filterSortCodeIds.length > 0) {
                const key = row.sortCodeId || row.sortCodeName || '';
                if (!filterSortCodeIds.includes(key)) return false;
            }
            if (filterDebitAccounts.length > 0 && !filterDebitAccounts.includes(row.debitAccount)) return false;
            if (filterCreditAccounts.length > 0 && !filterCreditAccounts.includes(row.creditAccount)) return false;
            if (filterStatus?.value === 'valid' && !(row.isValid && !row.isDuplicate)) return false;
            if (filterStatus?.value === 'duplicate' && !row.isDuplicate) return false;
            if (filterStatus?.value === 'error' && !(!row.isValid && !row.isDuplicate)) return false;
            return true;
        });
    }, [mdbDateFilteredEntries, filterSortCodeIds, filterDebitAccounts, filterCreditAccounts, filterStatus]);

    const previewFilterFields = useMemo(() => [
        { value: 'lineNumber', label: t('Line') || '#', type: 'number' },
        { value: 'documentDate', label: t('DocumentDate') || 'Document Date', type: 'date' },
        { value: 'registrationMonth', label: t('RegistrationMonth') || 'Registration Month', type: 'date' },
        { value: 'reportingMonth', label: t('ReportingMonth') || 'Reporting Month', type: 'date' },
        { value: 'reference', label: t('Reference') || 'Reference', type: 'text' },
        { value: 'reference2', label: t('Reference2') || 'Reference 2', type: 'text' },
        { value: 'debitAccount', label: t('DebitAccount') || 'Debit', type: 'text' },
        { value: 'debitAccountName', label: `${t('DebitAccount') || 'Debit'} - ${t('Name') || 'Name'}`, type: 'text' },
        { value: 'creditAccount', label: t('CreditAccount') || 'Credit', type: 'text' },
        { value: 'creditAccountName', label: `${t('CreditAccount') || 'Credit'} - ${t('Name') || 'Name'}`, type: 'text' },
        { value: 'amount', label: t('Amount') || 'Amount', type: 'number' },
        { value: 'vat', label: t('VAT') || 'VAT %', type: 'number' },
        { value: 'sortCodeId', label: t('SortCode Id') || 'Sort Code Id', type: 'text' },
        { value: 'sortCodeName', label: t('SortCodeName') || 'Sort Code', type: 'text' },
        { value: 'classification', label: t('Classification') || 'סיווג', type: 'classification' },
        { value: 'description', label: t('Description') || 'Description', type: 'text' },
        { value: 'confirmationNumber', label: t('ConfirmationNumber') || 'Confirmation', type: 'text' },
        { value: 'status', label: t('Status') || 'Status', type: 'status' },
    ], [t]);

    const previewFilterOperators = useMemo(() => ({
        text: [
            { value: 'contains', label: t('FilterContains') || 'מכיל' },
            { value: 'notContains', label: t('FilterNotContains') || 'לא מכיל' },
            { value: 'equals', label: t('FilterEquals') || 'שווה ל' },
            { value: 'notEquals', label: t('FilterNotEquals') || 'שונה מ' },
            { value: 'startsWith', label: t('FilterStartsWith') || 'מתחיל ב' },
            { value: 'isEmpty', label: t('FilterIsEmpty') || 'ריק' },
            { value: 'isNotEmpty', label: t('FilterIsNotEmpty') || 'לא ריק' },
        ],
        number: [
            { value: 'equals', label: '=' },
            { value: 'notEquals', label: '≠' },
            { value: 'greaterThan', label: '>' },
            { value: 'greaterOrEqual', label: '≥' },
            { value: 'lessThan', label: '<' },
            { value: 'lessOrEqual', label: '≤' },
            { value: 'between', label: t('FilterBetween') || 'בין' },
            { value: 'isEmpty', label: t('FilterIsEmpty') || 'ריק' },
        ],
        date: [
            { value: 'on', label: t('FilterOn') || 'בתאריך' },
            { value: 'before', label: t('FilterBefore') || 'לפני' },
            { value: 'after', label: t('FilterAfter') || 'אחרי' },
            { value: 'between', label: t('FilterBetween') || 'בין' },
            { value: 'isEmpty', label: t('FilterIsEmpty') || 'ריק' },
        ],
        status: [
            { value: 'equals', label: t('FilterEquals') || 'שווה ל' },
            { value: 'notEquals', label: t('FilterNotEquals') || 'שונה מ' },
        ],
        classification: [
            { value: 'equals', label: t('FilterEquals') || 'שווה ל' },
            { value: 'notEquals', label: t('FilterNotEquals') || 'שונה מ' },
        ],
    }), [t]);

    const previewClassificationValues = useMemo(() => [
        { value: 'Income', label: t('Income') || 'הכנסה' },
        { value: 'Expense', label: t('Expense') || 'הוצאה' },
        { value: 'ExpenseEquipment', label: t('ExpenseEquipment') || 'הוצאת ציוד' },
        { value: 'Other', label: t('Other') || 'אחר' },
    ], [t]);

    const formatClassification = (value) =>
        previewClassificationValues.find(o => o.value === value)?.label || value || '—';

    const previewStatusValues = useMemo(() => [
        { value: 'valid', label: t('Valid') || 'Valid' },
        { value: 'duplicate', label: t('Duplicate') || 'Duplicate' },
        { value: 'excluded', label: t('ExcludedByVatReport') || 'Excluded (VAT report)' },
        { value: 'error', label: t('Errors') || 'Error' },
    ], [t]);

    const getPreviewFilterField = (fieldValue) =>
        previewFilterFields.find(f => f.value === fieldValue) || previewFilterFields[0];

    const getPreviewRowStatus = (row) => {
        if (row.isDuplicate) return 'duplicate';
        if (row.isValid) return 'valid';
        if (row.errorMessage === 'ExcludedByVatReport') return 'excluded';
        return 'error';
    };

    const isPreviewRuleReady = (rule) => {
        if (!rule?.field || !rule?.operator) return false;
        if (rule.operator === 'isEmpty' || rule.operator === 'isNotEmpty') return true;
        if (rule.operator === 'between') return rule.value !== '' && rule.value2 !== '' && rule.value != null && rule.value2 != null;
        return rule.value !== '' && rule.value != null;
    };

    const matchesPreviewRule = (row, rule) => {
        const field = getPreviewFilterField(rule.field);
        const raw = field.value === 'status' ? getPreviewRowStatus(row) : row[field.value];
        const isBlank = raw == null || String(raw).trim() === '';

        if (rule.operator === 'isEmpty') return isBlank;
        if (rule.operator === 'isNotEmpty') return !isBlank;

        if (field.type === 'number') {
            if (isBlank) return false;
            const num = Number(raw);
            const target = Number(rule.value);
            if (Number.isNaN(num) || Number.isNaN(target)) return false;
            switch (rule.operator) {
                case 'equals': return num === target;
                case 'notEquals': return num !== target;
                case 'greaterThan': return num > target;
                case 'greaterOrEqual': return num >= target;
                case 'lessThan': return num < target;
                case 'lessOrEqual': return num <= target;
                case 'between': return num >= target && num <= Number(rule.value2);
                default: return true;
            }
        }

        if (field.type === 'date') {
            if (isBlank) return false;
            // Preview dates are ISO strings, so plain string compare keeps the order.
            const value = String(raw).slice(0, 10);
            const target = String(rule.value).slice(0, 10);
            switch (rule.operator) {
                case 'on': return value === target;
                case 'before': return value < target;
                case 'after': return value > target;
                case 'between': return value >= target && value <= String(rule.value2).slice(0, 10);
                default: return true;
            }
        }

        const value = String(raw ?? '').toLowerCase();
        const target = String(rule.value ?? '').toLowerCase();
        switch (rule.operator) {
            case 'contains': return value.includes(target);
            case 'notContains': return !value.includes(target);
            case 'equals': return value === target;
            case 'notEquals': return value !== target;
            case 'startsWith': return value.startsWith(target);
            default: return true;
        }
    };

    const mdbRuleFilteredEntries = useMemo(() => {
        const activeRules = previewFilterRules.filter(isPreviewRuleReady);
        if (activeRules.length === 0) return mdbColumnFilteredEntries;
        return mdbColumnFilteredEntries.filter(row => (previewFilterMatchAll
            ? activeRules.every(rule => matchesPreviewRule(row, rule))
            : activeRules.some(rule => matchesPreviewRule(row, rule))));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mdbColumnFilteredEntries, previewFilterRules, previewFilterMatchAll, previewFilterFields]);

    const addPreviewFilterRule = () => {
        setPreviewFilterRules(prev => [...prev, {
            id: `rule-${Date.now()}-${prev.length}`,
            field: 'reference',
            operator: 'contains',
            value: '',
            value2: '',
        }]);
    };

    const updatePreviewFilterRule = (id, changes) => {
        setPreviewFilterRules(prev => prev.map(rule => {
            if (rule.id !== id) return rule;
            const next = { ...rule, ...changes };
            if (changes.field && changes.field !== rule.field) {
                // Operators differ per field type, so restart the rule on the new field.
                const type = getPreviewFilterField(changes.field).type;
                next.operator = previewFilterOperators[type][0].value;
                next.value = '';
                next.value2 = '';
            }
            return next;
        }));
        setMdbPreviewPage(0);
    };

    const removePreviewFilterRule = (id) => {
        setPreviewFilterRules(prev => prev.filter(rule => rule.id !== id));
        setMdbPreviewPage(0);
    };

    const mdbFilteredEntries = useMemo(() => {
        if (!mdbPreviewSearch.trim()) return mdbRuleFilteredEntries;
        const term = mdbPreviewSearch.trim().toLowerCase();
        return mdbRuleFilteredEntries.filter(row =>
            [row.debitAccount, row.debitAccountName, row.creditAccount, row.creditAccountName,
            row.description, row.reference, row.reference2, row.sortCodeId, row.sortCodeName, row.classification, row.confirmationNumber,
            row.documentDate, row.registrationMonth, row.reportingMonth, String(row.amount ?? '')]
                .some(val => val && String(val).toLowerCase().includes(term))
        );
    }, [mdbRuleFilteredEntries, mdbPreviewSearch]);

    const getComparator = (sortOrder, sortField) => (a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        const isNum = (v) => v != null && v !== '' && !Number.isNaN(Number(v));
        if (sortOrder === 'asc') {
            if (isNum(aVal) && isNum(bVal)) return Number(aVal) - Number(bVal);
            return String(aVal ?? '').localeCompare(String(bVal ?? ''), undefined, { numeric: true });
        }
        if (isNum(aVal) && isNum(bVal)) return Number(bVal) - Number(aVal);
        return String(bVal ?? '').localeCompare(String(aVal ?? ''), undefined, { numeric: true });
    };

    const mdbSortedEntries = useMemo(() => {
        if (!mdbFilteredEntries.length) return [];
        return [...mdbFilteredEntries].sort(getComparator(order, orderBy));
    }, [mdbFilteredEntries, order, orderBy]);

    const mdbPaginatedEntries = useMemo(() =>
        mdbSortedEntries.slice(
            mdbPreviewPage * mdbPreviewRowsPerPage,
            mdbPreviewPage * mdbPreviewRowsPerPage + mdbPreviewRowsPerPage
        ), [mdbSortedEntries, mdbPreviewPage, mdbPreviewRowsPerPage]);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setMdbPreviewPage(0);
    };

    const renderSortHeader = (id, label, align = 'left') => (
        <TableCell align={align} sx={{ fontWeight: 700, fontSize: 12 }}>
            <TableSortLabel
                active={orderBy === id}
                direction={orderBy === id ? order : 'asc'}
                onClick={() => handleRequestSort(id)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    const mdbFormatAmount = (amount) => {
        if (amount == null) return '';
        return new Intl.NumberFormat('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
    };

    const mdbFormatMonth = (dateStr) => {
        if (!dateStr) return '—';
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return dateStr;
        return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    };

    const mdbPreviewSummaries = useMemo(() => {
        const valid = mdbRuleFilteredEntries.filter(e => e.isValid && !(e.isDuplicate && mdbSkipDuplicates));
        const sumAmount = valid.reduce((s, e) => s + (Number(e.amount) || 0), 0);
        const sumDebit = valid.filter(e => e.debitAccount).reduce((s, e) => s + (Number(e.amount) || 0), 0);
        const sumCredit = valid.filter(e => e.creditAccount).reduce((s, e) => s + (Number(e.amount) || 0), 0);
        let sumNet = 0;
        let sumVat = 0;
        valid.forEach(e => {
            const amt = Number(e.amount) || 0;
            const vatPct = e.vat != null ? Number(e.vat) : 0;
            if (vatPct > 0) {
                const net = amt / (1 + vatPct / 100);
                sumNet += net;
                sumVat += amt - net;
            } else {
                sumNet += amt;
            }
        });

        const normalizeVatKey = (vat) => {
            if (vat == null || vat === '') return 0;
            const n = Number(vat);
            return Number.isNaN(n) ? 0 : Math.round(n * 1000) / 1000;
        };

        const formatVatBreakdown = (byVatMap) => {
            return Array.from(byVatMap.entries())
                .map(([vat, stats]) => ({ vat, count: stats.count, sum: stats.sum }))
                .sort((a, b) => b.vat - a.vat);
        };

        const groupByMonth = (key) => {
            const map = new Map();
            valid.forEach(e => {
                const monthKey = e[key] || '—';
                const vatKey = normalizeVatKey(e.vat);
                const prev = map.get(monthKey) || { month: monthKey, count: 0, sum: 0, byVat: new Map() };
                prev.count += 1;
                prev.sum += Number(e.amount) || 0;
                const vatPrev = prev.byVat.get(vatKey) || { count: 0, sum: 0 };
                vatPrev.count += 1;
                vatPrev.sum += Number(e.amount) || 0;
                prev.byVat.set(vatKey, vatPrev);
                map.set(monthKey, prev);
            });
            return Array.from(map.values())
                .map(row => ({
                    month: row.month,
                    count: row.count,
                    sum: row.sum,
                    byVat: formatVatBreakdown(row.byVat),
                }))
                .sort((a, b) => String(a.month).localeCompare(String(b.month)));
        };

        const vatTotalsMap = new Map();
        valid.forEach(e => {
            const vatKey = normalizeVatKey(e.vat);
            const prev = vatTotalsMap.get(vatKey) || { count: 0, sum: 0 };
            prev.count += 1;
            prev.sum += Number(e.amount) || 0;
            vatTotalsMap.set(vatKey, prev);
        });

        const toYearMonth = (dateStr) => {
            if (!dateStr) return null;
            const d = new Date(dateStr);
            if (Number.isNaN(d.getTime())) return null;
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        };

        const mismatchedMap = new Map();
        let mismatchedCount = 0;
        let mismatchedSum = 0;
        valid.forEach(e => {
            const docYm = toYearMonth(e.documentDate);
            const repYm = toYearMonth(e.reportingMonth);
            if (!docYm || !repYm || docYm === repYm) return;
            mismatchedCount += 1;
            mismatchedSum += Number(e.amount) || 0;
            const key = `${docYm}|${repYm}`;
            const prev = mismatchedMap.get(key) || {
                documentMonth: e.documentDate,
                reportingMonth: e.reportingMonth,
                count: 0,
                sum: 0,
            };
            prev.count += 1;
            prev.sum += Number(e.amount) || 0;
            mismatchedMap.set(key, prev);
        });

        return {
            sumAmount,
            sumDebit,
            sumCredit,
            sumNet,
            sumVat,
            byReportingMonth: groupByMonth('reportingMonth'),
            byRegistrationMonth: groupByMonth('registrationMonth'),
            byVatTotal: formatVatBreakdown(vatTotalsMap),
            mismatchedMonths: Array.from(mismatchedMap.values()).sort((a, b) =>
                String(a.documentMonth).localeCompare(String(b.documentMonth)) ||
                String(a.reportingMonth).localeCompare(String(b.reportingMonth))
            ),
            mismatchedCount,
            mismatchedSum,
            filteredCount: mdbRuleFilteredEntries.length,
        };
    }, [mdbRuleFilteredEntries, mdbSkipDuplicates]);

    if (!open) return null;

    return (
        <Portal>
            <div
                className={`${styles.overlay} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}
                onClick={handleClose}
            >
                <div dir={direction} onClick={e => e.stopPropagation()}>
                    <div className={`${styles.modal} ${mode === 'journal' && mdbPreviewDone ? styles.modalWide : ''}`}>
                        <div className={styles.scroll}>
                            <div className={styles.header}>
                                <Tooltip title={t('Close') || 'Close'}>
                                    <IconButton onClick={handleClose} className={styles.closeIcon}>
                                        <CancelIcon className={styles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div>
                                    {"חשבשבת"}-MDB {mode === 'journal' ? 'יבוא פקודות יומן' : 'יבוא כרטיסים'}
                                </div>
                                <div />
                            </div>

                    <div className={styles.uploadGrid}>
                        {/* MDB File Upload */}
                        <div>
                            <div className={styles.sectionTitle}>{t('UploadFile')} (.mdb)</div>
                            <DropzoneArea
                                key={mdbFile ? mdbFile.name : 'mdb-empty'}
                                onDrop={handleMdbDrop}
                                accept=".mdb"
                                filesLimit={1}
                            />
                            {mdbFile && (
                                <div className={styles.fileMeta}>
                                    <span>{t('FileSelected')}: {mdbFile.name}</span>
                                </div>
                            )}
                        </div>

                        {/* Sortcode Excel Upload (journal mode only) */}
                        {mode === 'journal' && (
                            <div>
                                <div className={styles.sectionTitle}>קובץ קוד סוגי תנועות (.xlsx/.xls) - אופציונלי</div>
                                <DropzoneArea
                                    key={mdbSortCodeFile ? mdbSortCodeFile.name : 'mdb-sortcode-empty'}
                                    onDrop={handleMdbSortCodeDrop}
                                    accept=".xls,.xlsx"
                                    filesLimit={1}
                                />
                                {mdbSortCodeFile && (
                                    <div className={styles.fileMetaRow}>
                                        <span className={styles.fileMeta}>{t('FileSelected')}: {mdbSortCodeFile.name}</span>
                                        <button type="button" className={styles.linkBtn} onClick={() => setMdbSortCodeFile(null)}>{t('Remove')}</button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Monthly VAT report Excel files (journal mode only) */}
                        {mode === 'journal' && (
                            <div>
                                <h5>
                                    {t('VatReportFiles') || 'קבצי דוח מע״מ חודשיים'} (.xls/.xlsx) - {t('Optional') || 'אופציונלי'}
                                </h5>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {t('VatReportFilesHint') ||
                                        'אופציונלי, ניתן לצרף כמה קבצים ולהוסיף עוד בכל שלב. בלי קבצים — ייבוא רגיל (MDB + קובץ קודי מיון בלבד). כשמצורפים: תנועות עם מע״מ ≠ 0 יישמרו רק אם מספר התנועה (או האסמכתא) קיים בקבצים; חודש הדיווח נלקח מעמודת "חודש מע״מ" יחד עם שנת תאריך האסמכתא. תנועות עם מע״מ 0 ייובאו כרגיל.'}
                                </Typography>
                                <DropzoneArea
                                    key={`mdb-vat-${mdbVatReportFiles.map(item => item.file.name).join('|') || 'empty'}`}
                                    onDrop={handleMdbVatReportDrop}
                                    accept=".xls,.xlsx"
                                    multiple
                                />
                                {mdbVatReportFiles.length > 0 && (
                                    <Box mt={1} display="flex" flexDirection="column" gap={1}>
                                        {mdbVatReportFiles.map(item => (
                                            <Box
                                                key={item.id}
                                                display="flex"
                                                flexWrap="wrap"
                                                alignItems="center"
                                                gap={1}
                                                sx={{
                                                    p: 1,
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ flex: 1, minWidth: 160 }}>
                                                    {item.file.name}
                                                </Typography>
                                                <TextField
                                                    type="month"
                                                    size="small"
                                                    label={t('VatReportMonthFallback') || 'חודש מע״מ (גיבוי)'}
                                                    value={item.yearMonth || ''}
                                                    onChange={e => updateMdbVatReportYearMonth(item.id, e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    helperText={t('VatReportMonthFallbackHint') || 'רק אם אין עמודת חודש מע״מ'}
                                                    sx={{ minWidth: 190 }}
                                                />
                                                <Button size="small" variant="text" onClick={() => removeMdbVatReportFile(item.id)}>
                                                    {t('Remove')}
                                                </Button>
                                            </Box>
                                        ))}
                                        <Button size="small" variant="text" onClick={() => { setMdbVatReportFiles([]); clearVatReportPreviewState(); }}>
                                            {t('ClearAll') || 'נקה הכל'}
                                        </Button>
                                    </Box>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    {mode === 'cards' ? (
                        <div className={styles.actions}>
                            <button
                                type="button"
                                className={styles.save}
                                onClick={async () => {
                                    await importMdbAccountCards(mdbFile);
                                    handleClose();
                                }}
                                disabled={!mdbFile}
                            >
                                {t('ImportAccountCards')}
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className={styles.filterRow}>
                                <Autocomplete
                                    options={reportingMonthSourceOptions}
                                    getOptionLabel={(option) => option?.label ?? ''}
                                    value={selectedReportingMonthSource}
                                    onChange={(e, newValue) => handleReportingMonthSourceChange(newValue)}
                                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                    disableClearable
                                    renderInput={(params) => (
                                        <TextField {...params} label={t('ReportingMonthSource') || t('ReportingMonth') || 'Reporting month source'} size="small"
                                            helperText={t('ReportingMonthSourceExpenseHint') || 'חל רק על הוצאות / ציוד (לפי סיווג או קוד מיון). הכנסות ואחרים לפי תאריך מסמך.'}
                                        />
                                    )}
                                    PopperComponent={PkudaSortCodeStyledPopper}
                                    sx={{ minWidth: 200 }}
                                />
                                <Autocomplete
                                    options={registrationMonthSourceOptions}
                                    getOptionLabel={(option) => option?.label ?? ''}
                                    value={selectedRegistrationMonthSource}
                                    onChange={(e, newValue) => handleRegistrationMonthSourceChange(newValue)}
                                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                    disableClearable
                                    renderInput={(params) => (
                                        <TextField {...params} label={t('RegistrationMonthSource') || 'מקור חודש הרישום'} size="small" />
                                    )}
                                    PopperComponent={PkudaSortCodeStyledPopper}
                                    sx={{ minWidth: 220 }}
                                />
                                <TextField
                                    type="date"
                                    size="small"
                                    label={t('DocumentDateFrom') || 'Document Date From'}
                                    value={documentDateFrom}
                                    onChange={e => { setDocumentDateFrom(e.target.value); setMdbPreviewPage(0); }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: 170 }}
                                />
                                <TextField
                                    type="date"
                                    size="small"
                                    label={t('DocumentDateTo') || 'Document Date To'}
                                    value={documentDateTo}
                                    onChange={e => { setDocumentDateTo(e.target.value); setMdbPreviewPage(0); }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: 170 }}
                                />
                                <TextField
                                    type="month"
                                    size="small"
                                    label={t('ReportingMonthFrom') || 'Reporting Month From'}
                                    value={reportingMonthFrom ? reportingMonthFrom.slice(0, 7) : ''}
                                    onChange={e => {
                                        setReportingMonthFrom(e.target.value ? `${e.target.value}-01` : '');
                                        setMdbPreviewPage(0);
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: 170 }}
                                />
                                <TextField
                                    type="month"
                                    size="small"
                                    label={t('ReportingMonthTo') || 'Reporting Month To'}
                                    value={reportingMonthTo ? reportingMonthTo.slice(0, 7) : ''}
                                    onChange={e => {
                                        setReportingMonthTo(e.target.value ? `${e.target.value}-01` : '');
                                        setMdbPreviewPage(0);
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: 170 }}
                                />
                            </div>

                            <div className={styles.actions}>
                                <button
                                    type="button"
                                    className={styles.save}
                                    onClick={() => handleMdbJournalPreview()}
                                    disabled={!mdbFile || mdbProcessing}
                                >
                                    {mdbProcessing ? (
                                        <CircularProgress size={22} sx={{ color: 'var(--xtram-white)' }} />
                                    ) : (
                                        t('Preview')
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.save} ${styles.successBtn}`}
                                    disabled={!mdbPreviewDone || mdbProcessing || mdbMissingIncomplete}
                                    onClick={handleMdbJournalCommit}
                                >
                                    {mdbProcessing ? t('Processing') : t('CreateAndImport')}
                                </button>
                            </div>

                            {/* Missing Sortcodes Validation Table */}
                            {mdbPreviewDone && (
                                <div className={styles.block}>
                                    {mdbEditableMissing.length === 0 ? (
                                        <p className={styles.successText}>{t('NoMissingSortCodes')}</p>
                                    ) : (
                                        <>
                                            <div className={styles.sectionTitle}>{t('MissingSortCodes')}</div>
                                            <div className={styles.filterRow}>
                                                <Autocomplete
                                                    options={formattedSortCodeVatType || []}
                                                    getOptionLabel={(option) => option?.label ?? ''}
                                                    value={mdbBulkVatType}
                                                    onChange={(e, newValue) => setMdbBulkVatType(newValue)}
                                                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                    renderInput={(params) => (
                                                        <TextField {...params} placeholder={t('SelectSortCodeVatType')} size="small" />
                                                    )}
                                                    PopperComponent={PkudaSortCodeStyledPopper}
                                                    sx={{ minWidth: 180 }}
                                                />
                                                <Autocomplete
                                                    options={formattedSortCodesGroupNamesOptions || []}
                                                    getOptionLabel={(option) => option?.label ?? ''}
                                                    value={mdbBulkGroup}
                                                    onChange={(e, newValue) => setMdbBulkGroup(newValue)}
                                                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                    renderInput={(params) => (
                                                        <TextField {...params} placeholder={t('SelectSortCodeGroupName')} size="small" />
                                                    )}
                                                    PopperComponent={PkudaSortCodeStyledPopper}
                                                    sx={{ minWidth: 180 }}
                                                />
                                                <Autocomplete
                                                    options={[{ label: t('Yes'), value: true }, { label: t('No'), value: false }]}
                                                    getOptionLabel={(option) => option?.label ?? ''}
                                                    value={mdbBulkPar}
                                                    onChange={(e, newValue) => setMdbBulkPar(newValue)}
                                                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                    renderInput={(params) => (
                                                        <TextField {...params} placeholder={t('participatesInProfitAndLoss')} size="small" />
                                                    )}
                                                    PopperComponent={PkudaSortCodeStyledPopper}
                                                    sx={{ minWidth: 140 }}
                                                />
                                                <Autocomplete
                                                    options={Constants.VATPercentage.map(v => ({ label: `${v}%`, value: v }))}
                                                    getOptionLabel={(option) => option?.label ?? ''}
                                                    value={mdbBulkVat}
                                                    onChange={(e, newValue) => setMdbBulkVat(newValue)}
                                                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                    renderInput={(params) => (
                                                        <TextField {...params} placeholder={t('VAT')} size="small" />
                                                    )}
                                                    PopperComponent={PkudaSortCodeStyledPopper}
                                                    sx={{ minWidth: 100 }}
                                                />
                                                <button
                                                    type="button"
                                                    className={styles.cancel}
                                                    disabled={!mdbBulkVatType && !mdbBulkGroup && !mdbBulkPar && !mdbBulkVat}
                                                    onClick={applyMdbBulk}
                                                >
                                                    {t('ApplyToSelected')}
                                                </button>
                                            </div>

                                            <div className={styles.tableWrap}>
                                            <table className={styles.sortcodeTable}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: '5%' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={mdbSelectedRows.length === mdbEditableMissing.length && mdbEditableMissing.length > 0}
                                                                onChange={e => setMdbSelectedRows(e.target.checked ? mdbEditableMissing.map(r => r.sortCodeId) : [])}
                                                            />
                                                        </th>
                                                        <th>{t('SortCode Id')}</th>
                                                        <th>{t('SortCode Name')}</th>
                                                        <th>{t('SelectSortCodeVatType')}</th>
                                                        <th>{t('SelectSortCodeGroupName')}</th>
                                                        <th>{t('participatesInProfitAndLoss')}</th>
                                                        <th>{t('VAT')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {mdbEditableMissing.map((sc, i) => (
                                                        <tr key={sc.sortCodeId}>
                                                            <td>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={mdbSelectedRows.includes(sc.sortCodeId)}
                                                                    onChange={e => setMdbSelectedRows(prev => e.target.checked ? [...prev, sc.sortCodeId] : prev.filter(id => id !== sc.sortCodeId))}
                                                                />
                                                            </td>
                                                            <td>
                                                                <TextField fullWidth value={sc.sortCodeId} variant="standard" InputProps={{ readOnly: true, disableUnderline: true, sx: { height: '48px', padding: '0 10px', borderRadius: '8px', background: '#F5F5F6', border: '1px solid transparent' } }} />
                                                            </td>
                                                            <td>
                                                                {(() => {
                                                                    const original = mdbOriginalMissingSortCodes[i];
                                                                    const wasOriginallyEmpty = !original || !original.sortCodeName || !original.sortCodeName.trim();
                                                                    const isEmpty = !sc.sortCodeName || !sc.sortCodeName.trim();

                                                                    return (
                                                                        <TextField
                                                                            fullWidth
                                                                            value={sc.sortCodeName || ''}
                                                                            variant="standard"
                                                                            onChange={e => updateMdbMissing(i, 'sortCodeName', e.target.value)}
                                                                            placeholder={t('EnterSortCodeName') || 'Enter sortcode name'}
                                                                            InputProps={{
                                                                                readOnly: !wasOriginallyEmpty, // Only read-only if it was originally filled
                                                                                disableUnderline: true,
                                                                                sx: {
                                                                                    height: '48px',
                                                                                    padding: '0 10px',
                                                                                    borderRadius: '8px',
                                                                                    background: isEmpty ? 'white' : '#F5F5F6',
                                                                                    border: isEmpty ? '1.5px solid #f44336' : '1px solid transparent',
                                                                                },
                                                                            }}
                                                                            inputProps={{
                                                                                style: {
                                                                                    textAlign: direction === 'ltr' ? 'left' : 'right',
                                                                                },
                                                                            }}
                                                                        />
                                                                    );
                                                                })()}
                                                            </td>
                                                            <td>
                                                                <Autocomplete
                                                                    options={formattedSortCodeVatType || []}
                                                                    getOptionLabel={(option) => option?.label ?? ''}
                                                                    value={formattedSortCodeVatType.find(opt => opt.value === sc.sortCodeVatType?.toString()?.trim()) || null}
                                                                    onChange={(e, newValue) => updateMdbMissing(i, 'sortCodeVatType', newValue?.value ?? null)}
                                                                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                                    renderInput={(params) => (
                                                                        <TextField {...params} placeholder={t('SelectSortCodeVatType')} size="small" error={!sc.sortCodeVatType} />
                                                                    )}
                                                                    PopperComponent={PkudaSortCodeStyledPopper}
                                                                />
                                                            </td>
                                                            <td>
                                                                <Autocomplete
                                                                    options={formattedSortCodesGroupNamesOptions || []}
                                                                    getOptionLabel={(option) => option?.label ?? ''}
                                                                    value={formattedSortCodesGroupNamesOptions.find(opt => opt.value === sc.sortCodeGroup) || null}
                                                                    onChange={(e, newValue) => updateMdbMissing(i, 'sortCodeGroup', newValue?.value ?? null)}
                                                                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                                    renderInput={(params) => (
                                                                        <TextField {...params} placeholder={t('SelectSortCodeGroupName')} size="small" />
                                                                    )}
                                                                    PopperComponent={PkudaSortCodeStyledPopper}
                                                                />
                                                            </td>
                                                            <td>
                                                                <Autocomplete
                                                                    options={[{ label: t('Yes'), value: true }, { label: t('No'), value: false }]}
                                                                    getOptionLabel={(option) => option?.label ?? ''}
                                                                    value={sc.participatesInProfitAndLoss === true ? { label: t('Yes'), value: true } : sc.participatesInProfitAndLoss === false ? { label: t('No'), value: false } : null}
                                                                    onChange={(e, newValue) => updateMdbMissing(i, 'participatesInProfitAndLoss', newValue?.value ?? null)}
                                                                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                                    renderInput={(params) => (
                                                                        <TextField {...params} placeholder={t('participatesInProfitAndLoss')} size="small" error={sc.participatesInProfitAndLoss === undefined || sc.participatesInProfitAndLoss === null} />
                                                                    )}
                                                                    PopperComponent={PkudaSortCodeStyledPopper}
                                                                />
                                                            </td>
                                                            <td>
                                                                <Autocomplete
                                                                    options={Constants.VATPercentage.map(v => ({ label: `${v}%`, value: v }))}
                                                                    getOptionLabel={(option) => option?.label ?? ''}
                                                                    value={Constants.VATPercentage.map(v => ({ label: `${v}%`, value: v })).find(opt => opt.value === sc.vat) || null}
                                                                    onChange={(e, newValue) => updateMdbMissing(i, 'vat', newValue?.value ?? null)}
                                                                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                                    renderInput={(params) => (
                                                                        <TextField {...params} placeholder={t('VAT')} size="small" error={sc.vat === undefined || sc.vat === null} />
                                                                    )}
                                                                    PopperComponent={PkudaSortCodeStyledPopper}
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Missing Account Cards Table */}
                            {mdbPreviewDone && (
                                <div className={styles.block}>
                                    <MdbMissingAccountCardsTable
                                        missingAccountCards={mdbMissingAccountCards}
                                        direction={direction}
                                        client={client}
                                        token={token}
                                        palCodes={palCodes || []}
                                        accountCardOptions={accountCardOptions}
                                        fetchEnums={fetchEnums}
                                        setLoading={setLoading}
                                        isRange={false}
                                        isClosedReportingMonth={false}
                                        sortCodes={[]}
                                        user={user}
                                        setAccountCardOptions={setAccountCardOptions}
                                        onCardCreated={async () => {
                                            if (mdbFile) {
                                                await handleMdbJournalPreview();
                                            }
                                        }}
                                    />
                                </div>
                            )}

                            {/* VAT Excel rows missing from MDB (only when VAT files uploaded) */}
                            {mdbPreviewDone && useVatReportLogic && (
                                <div style={{ marginTop: 25 }}>
                                    <MissingVatReportRowsTable
                                        rows={missingVatRows.rows}
                                        selectedIds={missingVatRows.selectedIds}
                                        onSelectedChange={missingVatRows.setSelectedIds}
                                        adding={missingVatRows.adding}
                                        onUpdateRow={missingVatRows.updateRow}
                                        onAddRows={missingVatRows.addRows}
                                        sortCodeOptions={sortCodeOptions}
                                        accountCardOptions={accountCardOptions}
                                        t={t}
                                        title={t('MissingVatReportRows') || 'שורות מדוח מע״מ שלא קיימות ב־MDB'}
                                        emptyMessage={t('NoMissingVatReportRows') || 'כל שורות דוח המע״מ נמצאו ב־MDB'}
                                        formatAmount={mdbFormatAmount}
                                        formatMonth={mdbFormatMonth}
                                    />
                                </div>
                            )}

                            {/* Card Classification editor */}
                            {mdbPreviewDone && (
                                <Box mt={3} p={2} component={Paper} variant="outlined">
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                                        {t('CardClassification') || 'סיווג כרטיסים'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                        {t('CardClassificationHint') ||
                                            'בחירה אוטומטית רק מכרטיסי התצוגה המקדימה לפי שם: הכנסות/מכירות/שכ״ט · הוצאות · ציוד. בלי מס / תשומות / עסקאות.'}
                                    </Typography>

                                    <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="flex-start" mb={2}>
                                        <Autocomplete
                                            multiple
                                            size="small"
                                            options={classificationCardOptions}
                                            value={classificationCardOptions.filter(o =>
                                                classificationPickerIds.includes(String(o.cardId)))}
                                            onChange={(_, selected) =>
                                                setClassificationPickerIds(selected.map(o => String(o.cardId)))}
                                            getOptionLabel={(option) => option?.label ?? ''}
                                            isOptionEqualToValue={(a, b) => String(a?.cardId) === String(b?.cardId)}
                                            renderOption={(props, option) => (
                                                <li {...props} key={option.cardId}>
                                                    <Box>
                                                        <Typography variant="body2">{option.label}</Typography>
                                                        {option.kind && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {classificationKindOptions.find(k => k.value === option.kind)?.label || option.kind}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </li>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={t('SelectCards') || 'בחירת כרטיסים'}
                                                    placeholder={t('Search') || 'חפש'}
                                                />
                                            )}
                                            PopperComponent={PkudaSortCodeStyledPopper}
                                            sx={{ minWidth: 280, flex: 2 }}
                                        />
                                        <Autocomplete
                                            size="small"
                                            options={classificationKindOptions}
                                            value={classificationKindOptions.find(o => o.value === classificationAssignKind) || null}
                                            onChange={(_, v) => setClassificationAssignKind(v?.value || null)}
                                            getOptionLabel={(option) => option?.label ?? ''}
                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                            renderInput={(params) => (
                                                <TextField {...params} label={t('Classification') || 'סיווג'} />
                                            )}
                                            PopperComponent={PkudaSortCodeStyledPopper}
                                            sx={{ minWidth: 180, flex: 1 }}
                                        />
                                        <BorderBoxButton
                                            variant="contained"
                                            disabled={!classificationPickerIds.length || !classificationAssignKind}
                                            onClick={() => {
                                                moveSelectedCardsToKind(classificationPickerIds, classificationAssignKind);
                                                setClassificationPickerIds([]);
                                            }}
                                            sx={{ mt: 0.5 }}
                                        >
                                            {t('AssignClassification') || 'שייך סיווג'}
                                        </BorderBoxButton>
                                        <BorderBoxButton
                                            variant="outlined"
                                            disabled={mdbProcessing || !mdbPreviewEntries.length}
                                            onClick={handleAutoSuggestClassification}
                                            sx={{ mt: 0.5 }}
                                        >
                                            {t('AutoSuggestClassification') || 'הצעה אוטומטית'}
                                        </BorderBoxButton>
                                        <BorderBoxButton
                                            variant="outlined"
                                            disabled={!mdbFile || mdbProcessing}
                                            onClick={handleApplyCardClassification}
                                            sx={{ mt: 0.5 }}
                                        >
                                            {t('ApplyCardClassification') || 'עדכן תצוגה מקדימה'}
                                        </BorderBoxButton>
                                    </Box>

                                    <Box display="grid" gap={1.5} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}>
                                        {classificationKindOptions.map(kindOpt => (
                                            <Autocomplete
                                                key={kindOpt.value}
                                                multiple
                                                size="small"
                                                options={classificationCardOptions}
                                                value={getClassificationOptionsForKind(kindOpt.value)}
                                                onChange={(_, selected) =>
                                                    setClassificationCardsForKind(
                                                        kindOpt.value,
                                                        selected.map(o => o.cardId)
                                                    )}
                                                getOptionLabel={(option) => option?.label ?? ''}
                                                isOptionEqualToValue={(a, b) => String(a?.cardId) === String(b?.cardId)}
                                                renderTags={(value, getTagProps) =>
                                                    value.map((option, index) => (
                                                        <Chip
                                                            {...getTagProps({ index })}
                                                            key={option.cardId}
                                                            size="small"
                                                            label={option.label}
                                                        />
                                                    ))
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={`${kindOpt.label} (${(mdbCardClassification?.[classificationKindKeys[kindOpt.value]] || []).length})`}
                                                    />
                                                )}
                                                PopperComponent={PkudaSortCodeStyledPopper}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {/* Preview Entries Table */}
                            {mdbPreviewData && (
                                <Box mt={3} sx={{ maxHeight: 600, overflow: 'auto' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {t('TotalLines') || 'Total lines'}: <strong>{mdbPreviewData.totalLines}</strong>
                                        {' | '}
                                        {t('ValidCount') || 'Valid'}: <strong>{mdbPreviewData.validCount}</strong>
                                        {' | '}
                                        {t('ErrorCount') || 'Errors'}: <strong>{mdbPreviewData.errorCount}</strong>
                                        {' | '}
                                        {t('Filtered') || 'Filtered'}: <strong>{mdbPreviewSummaries.filteredCount}</strong>
                                        {(mdbPreviewData.duplicateCount ?? 0) > 0 && (
                                            <>
                                                {' | '}
                                                <span style={{ color: '#ed6c02' }}>
                                                    {t('Duplicates') || 'Duplicates'}: <strong>{mdbPreviewData.duplicateCount}</strong>
                                                </span>
                                            </>
                                        )}
                                        {useVatReportLogic && (mdbPreviewData.excludedByVatReportCount ?? 0) > 0 && (
                                            <>
                                                {' | '}
                                                <span style={{ color: '#d32f2f' }}>
                                                    {t('ExcludedByVatReport') || 'Excluded (VAT report)'}: <strong>{mdbPreviewData.excludedByVatReportCount}</strong>
                                                </span>
                                            </>
                                        )}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                        {t('SumAmount')}: <strong>{mdbFormatAmount(mdbPreviewSummaries.sumAmount)}</strong>
                                        {' | '}
                                        {t('SumNetAmount')}: <strong>{mdbFormatAmount(mdbPreviewSummaries.sumNet)}</strong>
                                        {' | '}
                                        {t('SumVatAmount')}: <strong>{mdbFormatAmount(mdbPreviewSummaries.sumVat)}</strong>
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {t('SumDebit')}: <strong>{mdbFormatAmount(mdbPreviewSummaries.sumDebit)}</strong>
                                        {' | '}
                                        {t('SumCredit')}: <strong>{mdbFormatAmount(mdbPreviewSummaries.sumCredit)}</strong>
                                    </Typography>

                                    <Box display="flex" flexWrap="wrap" gap={2} mb={1.5}>
                                        <Box sx={{ minWidth: 320, flex: 1 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                                {t('ReportingMonth') || 'Reporting Month'}
                                            </Typography>
                                            {useVatReportLogic && (
                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                    {t('ReportingMonthVatHint') ||
                                                        'לחודשי דיווח של תנועות עם מע״מ ≠ 0 — לפי חודש דוח המע״מ שבחרתם לקובץ. תנועות עם מע״מ 0 נשארות לפי מקור חודש הדיווח הרגיל.'}
                                                </Typography>
                                            )}
                                            <TableContainer component={Paper} variant="outlined">
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: 11, py: 0.5 }}>{t('Month') || 'Month'}</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, py: 0.5 }}>{t('Count') || '#'}</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, py: 0.5 }}>{t('Amount')}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: 11, py: 0.5 }}>{t('ByVat') || 'לפי מע״מ'}</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {mdbPreviewSummaries.byReportingMonth.map(row => (
                                                            <TableRow key={`rep-${row.month}`}>
                                                                <TableCell sx={{ fontSize: 11, py: 0.3 }}>{mdbFormatMonth(row.month)}</TableCell>
                                                                <TableCell align="right" sx={{ fontSize: 11, py: 0.3 }}>{row.count}</TableCell>
                                                                <TableCell align="right" sx={{ fontSize: 11, py: 0.3 }}>{mdbFormatAmount(row.sum)}</TableCell>
                                                                <TableCell sx={{ fontSize: 11, py: 0.3 }}>
                                                                    {(row.byVat || []).map(v => (
                                                                        <div key={`rep-vat-${row.month}-${v.vat}`}>
                                                                            {t('VatPercentCount', { vat: v.vat, count: v.count }) ||
                                                                                `מע״מ ${v.vat}%: ${v.count}`}
                                                                        </div>
                                                                    ))}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                        {mdbPreviewSummaries.byReportingMonth.length === 0 && (
                                                            <TableRow>
                                                                <TableCell colSpan={4} sx={{ fontSize: 11 }}>—</TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                        <Box sx={{ minWidth: 320, flex: 1 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                                {t('RegistrationMonth') || 'Registration Month'}
                                            </Typography>
                                            {useVatReportLogic && (
                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                    {t('RegistrationMonthHint') ||
                                                        'חודש רישום = חודש תאריך המסמך (ValueDate). לכן יכולים להופיע 11/2025 ו־12/2025 גם כשדוחות המע״מ הם 01–06/2026.'}
                                                </Typography>
                                            )}
                                            <TableContainer component={Paper} variant="outlined">
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: 11, py: 0.5 }}>{t('Month') || 'Month'}</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, py: 0.5 }}>{t('Count') || '#'}</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, py: 0.5 }}>{t('Amount')}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: 11, py: 0.5 }}>{t('ByVat') || 'לפי מע״מ'}</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {mdbPreviewSummaries.byRegistrationMonth.map(row => (
                                                            <TableRow key={`reg-${row.month}`}>
                                                                <TableCell sx={{ fontSize: 11, py: 0.3 }}>{mdbFormatMonth(row.month)}</TableCell>
                                                                <TableCell align="right" sx={{ fontSize: 11, py: 0.3 }}>{row.count}</TableCell>
                                                                <TableCell align="right" sx={{ fontSize: 11, py: 0.3 }}>{mdbFormatAmount(row.sum)}</TableCell>
                                                                <TableCell sx={{ fontSize: 11, py: 0.3 }}>
                                                                    {(row.byVat || []).map(v => (
                                                                        <div key={`reg-vat-${row.month}-${v.vat}`}>
                                                                            {t('VatPercentCount', { vat: v.vat, count: v.count }) ||
                                                                                `מע״מ ${v.vat}%: ${v.count}`}
                                                                        </div>
                                                                    ))}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                        {mdbPreviewSummaries.byRegistrationMonth.length === 0 && (
                                                            <TableRow>
                                                                <TableCell colSpan={4} sx={{ fontSize: 11 }}>—</TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    </Box>

                                    <Box mb={1.5} sx={{ minWidth: 280, maxWidth: 480 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                            {t('VatBreakdownTotal') || 'סיכום לפי אחוז מע״מ'}
                                        </Typography>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 11, py: 0.5 }}>{t('VAT') || 'VAT %'}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, py: 0.5 }}>{t('Count') || '#'}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, py: 0.5 }}>{t('Amount')}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {(mdbPreviewSummaries.byVatTotal || []).map(row => (
                                                        <TableRow key={`vat-total-${row.vat}`}>
                                                            <TableCell sx={{ fontSize: 11, py: 0.3 }}>{`${row.vat}%`}</TableCell>
                                                            <TableCell align="right" sx={{ fontSize: 11, py: 0.3 }}>{row.count}</TableCell>
                                                            <TableCell align="right" sx={{ fontSize: 11, py: 0.3 }}>{mdbFormatAmount(row.sum)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                    {(mdbPreviewSummaries.byVatTotal || []).length === 0 && (
                                                        <TableRow>
                                                            <TableCell colSpan={3} sx={{ fontSize: 11 }}>—</TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>

                                    <Box mb={1.5}>
                                        <Typography variant="subtitle2" sx={{ mb: 0.5 }} color="warning.dark">
                                            {t('DocumentReportingMonthMismatch') || 'Document month ≠ Reporting month'}
                                            {': '}
                                            <strong>{mdbPreviewSummaries.mismatchedCount}</strong>
                                            {' | '}
                                            {t('Amount')}: <strong>{mdbFormatAmount(mdbPreviewSummaries.mismatchedSum)}</strong>
                                        </Typography>
                                        <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: '#fff8e1' }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 11, py: 0.5 }}>
                                                            {t('DocumentDate') || 'Document Date'} ({t('Month') || 'Month'})
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 11, py: 0.5 }}>
                                                            {t('ReportingMonth') || 'Reporting Month'}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, py: 0.5 }}>
                                                            {t('Count') || '#'}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, py: 0.5 }}>
                                                            {t('Amount')}
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {mdbPreviewSummaries.mismatchedMonths.map(row => (
                                                        <TableRow key={`mismatch-${row.documentMonth}-${row.reportingMonth}`}>
                                                            <TableCell sx={{ fontSize: 11, py: 0.3 }}>{mdbFormatMonth(row.documentMonth)}</TableCell>
                                                            <TableCell sx={{ fontSize: 11, py: 0.3 }}>{mdbFormatMonth(row.reportingMonth)}</TableCell>
                                                            <TableCell align="right" sx={{ fontSize: 11, py: 0.3 }}>{row.count}</TableCell>
                                                            <TableCell align="right" sx={{ fontSize: 11, py: 0.3 }}>{mdbFormatAmount(row.sum)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                    {mdbPreviewSummaries.mismatchedMonths.length === 0 && (
                                                        <TableRow>
                                                            <TableCell colSpan={4} sx={{ fontSize: 11 }}>
                                                                {t('NoMismatchedMonths') || 'No rows with different document/reporting months'}
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>

                                    {(mdbPreviewData.duplicateCount ?? 0) > 0 && (() => {
                                        const dupEntries = mdbPreviewEntries.filter(e => e.isDuplicate);
                                        return (
                                            <Box mb={1.5}>
                                                <Box p={1} bgcolor="#fff3e0" borderRadius={1} border="1px solid #ffb74d" display="flex" alignItems="center" gap={1}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={mdbSkipDuplicates}
                                                                onChange={e => setMdbSkipDuplicates(e.target.checked)}
                                                                size="small"
                                                            />
                                                        }
                                                        label={
                                                            <Typography variant="body2">
                                                                {t('SkipDuplicateEntries')} ({mdbPreviewData.duplicateCount} {t('Duplicates')})
                                                            </Typography>
                                                        }
                                                    />
                                                </Box>
                                                <Box mt={1} sx={{ maxHeight: 250, overflow: 'auto' }}>
                                                    <Typography variant="subtitle2" color="warning.dark" sx={{ mb: 0.5 }}>
                                                        {t('DuplicateEntries')} ({dupEntries.length})
                                                    </Typography>
                                                    <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: '#fff8e1' }}>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell sx={{ fontWeight: 700, fontSize: 11, py: 0.5, px: 1 }}>{t('Line')}</TableCell>
                                                                    <TableCell sx={{ fontWeight: 700, fontSize: 11, py: 0.5, px: 1 }}>{t('DocumentDate')}</TableCell>
                                                                    <TableCell sx={{ fontWeight: 700, fontSize: 11, py: 0.5, px: 1 }}>{t('DebitAccount')}</TableCell>
                                                                    <TableCell sx={{ fontWeight: 700, fontSize: 11, py: 0.5, px: 1 }}>{t('CreditAccount')}</TableCell>
                                                                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, py: 0.5, px: 1 }}>{t('Amount')}</TableCell>
                                                                    <TableCell sx={{ fontWeight: 700, fontSize: 11, py: 0.5, px: 1 }}>{t('Reference')}</TableCell>
                                                                    <TableCell sx={{ fontWeight: 700, fontSize: 11, py: 0.5, px: 1 }}>{t('Description')}</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {dupEntries.map((row, i) => (
                                                                    <TableRow key={i}>
                                                                        <TableCell sx={{ fontSize: 11, py: 0.3, px: 1 }}>{row.lineNumber}</TableCell>
                                                                        <TableCell sx={{ fontSize: 11, py: 0.3, px: 1, whiteSpace: 'nowrap' }}>{row.documentDate ?? '—'}</TableCell>
                                                                        <TableCell sx={{ fontSize: 11, py: 0.3, px: 1 }}>{row.debitAccount}{row.debitAccountName ? ` (${row.debitAccountName})` : ''}</TableCell>
                                                                        <TableCell sx={{ fontSize: 11, py: 0.3, px: 1 }}>{row.creditAccount}{row.creditAccountName ? ` (${row.creditAccountName})` : ''}</TableCell>
                                                                        <TableCell align="right" sx={{ fontSize: 11, py: 0.3, px: 1 }}>{mdbFormatAmount(row.amount)}</TableCell>
                                                                        <TableCell sx={{ fontSize: 11, py: 0.3, px: 1 }}>{row.reference ?? '—'}</TableCell>
                                                                        <TableCell sx={{ fontSize: 11, py: 0.3, px: 1, maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.description}>{row.description ?? ''}</TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Box>
                                            </Box>
                                        );
                                    })()}

                                    {useVatReportLogic && (mdbPreviewData.excludedByVatReportCount ?? 0) > 0 && (
                                        <Box mb={2} p={1.5} bgcolor="#fff8e1" borderRadius={1} border="1px solid #ffb74d">
                                            <Typography variant="body2" color="warning.dark">
                                                {t('ExcludedByVatReportSummary', {
                                                    count: mdbPreviewData.excludedByVatReportCount,
                                                }) ||
                                                    `${mdbPreviewData.excludedByVatReportCount} entries with VAT ≠ 0 were excluded because their reference was not found in the VAT report files. Matched entries use the VAT month you selected for each file.`}
                                            </Typography>
                                        </Box>
                                    )}

                                    {(() => {
                                        const visibleErrors = (mdbPreviewData.errorMessages || []).filter(
                                            msg => !String(msg).startsWith('ExcludedByVatReportSummary:')
                                        );
                                        if (visibleErrors.length === 0) return null;
                                        const capped = visibleErrors.slice(0, 30);
                                        return (
                                            <Box mb={2} p={1.5} bgcolor="error.light" borderRadius={1} sx={{ maxHeight: 180, overflow: 'auto' }}>
                                                <Typography variant="subtitle2" color="error.dark" sx={{ mb: 0.5 }}>
                                                    {t('Errors') || 'Errors'} ({visibleErrors.length})
                                                </Typography>
                                                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                                    {capped.map((msg, i) => (
                                                        <li key={i}>
                                                            <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                {msg}
                                                            </Typography>
                                                        </li>
                                                    ))}
                                                    {visibleErrors.length > capped.length && (
                                                        <li>
                                                            <Typography variant="body2" component="span">
                                                                {t('AndMoreErrors', { count: visibleErrors.length - capped.length }) ||
                                                                    `… and ${visibleErrors.length - capped.length} more`}
                                                            </Typography>
                                                        </li>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    })()}

                                    <Box display="flex" flexWrap="wrap" gap={1.5} mb={1.5} alignItems="center">
                                        <TextField
                                            size="small"
                                            placeholder={t('Search') || 'Search...'}
                                            value={mdbPreviewSearch}
                                            onChange={e => { setMdbPreviewSearch(e.target.value); setMdbPreviewPage(0); }}
                                            sx={{ width: 260 }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <Autocomplete
                                            multiple
                                            size="small"
                                            options={sortCodeFilterOptions}
                                            getOptionLabel={(option) => option?.label ?? ''}
                                            value={sortCodeFilterOptions.filter(o => filterSortCodeIds.includes(o.value))}
                                            onChange={(e, newValue) => {
                                                setFilterSortCodeIds(newValue.map(v => v.value));
                                                setMdbPreviewPage(0);
                                            }}
                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                            renderInput={(params) => (
                                                <TextField {...params} label={t('SortCode Id') || t('SortCodeName') || 'Sort Code'} />
                                            )}
                                            PopperComponent={PkudaSortCodeStyledPopper}
                                            sx={{ minWidth: 240 }}
                                        />
                                        <Autocomplete
                                            multiple
                                            size="small"
                                            options={debitAccountOptions}
                                            value={filterDebitAccounts}
                                            onChange={(e, newValue) => {
                                                setFilterDebitAccounts(newValue);
                                                setMdbPreviewPage(0);
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} label={t('DebitAccount') || 'Debit'} />
                                            )}
                                            PopperComponent={PkudaSortCodeStyledPopper}
                                            sx={{ minWidth: 180 }}
                                        />
                                        <Autocomplete
                                            multiple
                                            size="small"
                                            options={creditAccountOptions}
                                            value={filterCreditAccounts}
                                            onChange={(e, newValue) => {
                                                setFilterCreditAccounts(newValue);
                                                setMdbPreviewPage(0);
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} label={t('CreditAccount') || 'Credit'} />
                                            )}
                                            PopperComponent={PkudaSortCodeStyledPopper}
                                            sx={{ minWidth: 180 }}
                                        />
                                        <Autocomplete
                                            size="small"
                                            options={statusFilterOptions}
                                            getOptionLabel={(option) => option?.label ?? ''}
                                            value={filterStatus}
                                            onChange={(e, newValue) => {
                                                setFilterStatus(newValue);
                                                setMdbPreviewPage(0);
                                            }}
                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                            renderInput={(params) => (
                                                <TextField {...params} label={t('Status') || 'Status'} />
                                            )}
                                            PopperComponent={PkudaSortCodeStyledPopper}
                                            sx={{ minWidth: 140 }}
                                        />
                                    </Box>

                                    <Box mb={1.5} p={1.5} sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                        <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={previewFilterRules.length ? 1 : 0}>
                                            <Typography variant="subtitle2" sx={{ mr: 1 }}>
                                                {t('AdvancedFilters') || 'מסננים חופשיים'}
                                            </Typography>
                                            <Button size="small" variant="outlined" onClick={addPreviewFilterRule}>
                                                {t('AddFilter') || 'הוסף מסנן'}
                                            </Button>
                                            {previewFilterRules.length > 1 && (
                                                <Autocomplete
                                                    size="small"
                                                    options={[
                                                        { value: true, label: t('MatchAllFilters') || 'כל התנאים' },
                                                        { value: false, label: t('MatchAnyFilter') || 'לפחות תנאי אחד' },
                                                    ]}
                                                    getOptionLabel={(option) => option?.label ?? ''}
                                                    value={previewFilterMatchAll
                                                        ? { value: true, label: t('MatchAllFilters') || 'כל התנאים' }
                                                        : { value: false, label: t('MatchAnyFilter') || 'לפחות תנאי אחד' }}
                                                    onChange={(e, newValue) => {
                                                        setPreviewFilterMatchAll(newValue?.value !== false);
                                                        setMdbPreviewPage(0);
                                                    }}
                                                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                    disableClearable
                                                    renderInput={(params) => <TextField {...params} label={t('MatchMode') || 'התאמה'} />}
                                                    PopperComponent={PkudaSortCodeStyledPopper}
                                                    sx={{ minWidth: 180 }}
                                                />
                                            )}
                                            {previewFilterRules.length > 0 && (
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={() => { setPreviewFilterRules([]); setMdbPreviewPage(0); }}
                                                >
                                                    {t('ClearFilters') || 'נקה מסננים'}
                                                </Button>
                                            )}
                                        </Box>

                                        {previewFilterRules.map(rule => {
                                            const field = getPreviewFilterField(rule.field);
                                            const operators = previewFilterOperators[field.type];
                                            const selectedOperator = operators.find(o => o.value === rule.operator) || operators[0];
                                            const needsValue = rule.operator !== 'isEmpty' && rule.operator !== 'isNotEmpty';
                                            const needsSecondValue = rule.operator === 'between';
                                            const valueType = field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text';
                                            return (
                                                <Box key={rule.id} display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={1}>
                                                    <Autocomplete
                                                        size="small"
                                                        options={previewFilterFields}
                                                        getOptionLabel={(option) => option?.label ?? ''}
                                                        value={field}
                                                        onChange={(e, newValue) => updatePreviewFilterRule(rule.id, { field: newValue?.value || 'reference' })}
                                                        isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                        disableClearable
                                                        renderInput={(params) => <TextField {...params} label={t('Field') || 'שדה'} />}
                                                        PopperComponent={PkudaSortCodeStyledPopper}
                                                        sx={{ minWidth: 200 }}
                                                    />
                                                    <Autocomplete
                                                        size="small"
                                                        options={operators}
                                                        getOptionLabel={(option) => option?.label ?? ''}
                                                        value={selectedOperator}
                                                        onChange={(e, newValue) => updatePreviewFilterRule(rule.id, { operator: newValue?.value || operators[0].value })}
                                                        isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                        disableClearable
                                                        renderInput={(params) => <TextField {...params} label={t('Condition') || 'תנאי'} />}
                                                        PopperComponent={PkudaSortCodeStyledPopper}
                                                        sx={{ minWidth: 160 }}
                                                    />
                                                    {needsValue && field.type === 'status' && (
                                                        <Autocomplete
                                                            size="small"
                                                            options={previewStatusValues}
                                                            getOptionLabel={(option) => option?.label ?? ''}
                                                            value={previewStatusValues.find(o => o.value === rule.value) || null}
                                                            onChange={(e, newValue) => updatePreviewFilterRule(rule.id, { value: newValue?.value ?? '' })}
                                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                            renderInput={(params) => <TextField {...params} label={t('Value') || 'ערך'} />}
                                                            PopperComponent={PkudaSortCodeStyledPopper}
                                                            sx={{ minWidth: 200 }}
                                                        />
                                                    )}
                                                    {needsValue && field.type === 'classification' && (
                                                        <Autocomplete
                                                            size="small"
                                                            options={previewClassificationValues}
                                                            getOptionLabel={(option) => option?.label ?? ''}
                                                            value={previewClassificationValues.find(o => o.value === rule.value) || null}
                                                            onChange={(e, newValue) => updatePreviewFilterRule(rule.id, { value: newValue?.value ?? '' })}
                                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                            renderInput={(params) => <TextField {...params} label={t('Value') || 'ערך'} />}
                                                            PopperComponent={PkudaSortCodeStyledPopper}
                                                            sx={{ minWidth: 200 }}
                                                        />
                                                    )}
                                                    {needsValue && field.type !== 'status' && field.type !== 'classification' && (
                                                        <TextField
                                                            size="small"
                                                            type={valueType}
                                                            label={needsSecondValue ? (t('From') || 'מ') : (t('Value') || 'ערך')}
                                                            value={rule.value ?? ''}
                                                            onChange={e => updatePreviewFilterRule(rule.id, { value: e.target.value })}
                                                            InputLabelProps={valueType === 'date' ? { shrink: true } : undefined}
                                                            sx={{ minWidth: 170 }}
                                                        />
                                                    )}
                                                    {needsSecondValue && field.type !== 'status' && (
                                                        <TextField
                                                            size="small"
                                                            type={valueType}
                                                            label={t('To') || 'עד'}
                                                            value={rule.value2 ?? ''}
                                                            onChange={e => updatePreviewFilterRule(rule.id, { value2: e.target.value })}
                                                            InputLabelProps={valueType === 'date' ? { shrink: true } : undefined}
                                                            sx={{ minWidth: 170 }}
                                                        />
                                                    )}
                                                    <IconButton size="small" onClick={() => removePreviewFilterRule(rule.id)}>
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            );
                                        })}
                                    </Box>

                                    {mdbFilteredEntries.length > 0 && (
                                        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        {renderSortHeader('lineNumber', t('Line') || '#')}
                                                        {renderSortHeader('documentDate', t('DocumentDate') || 'Date')}
                                                        {renderSortHeader('registrationMonth', t('RegistrationMonth') || 'Register Month')}
                                                        {renderSortHeader('reportingMonth', t('ReportingMonth') || 'Reporting Month')}
                                                        {renderSortHeader('reference', t('Reference'))}
                                                        {renderSortHeader('debitAccount', t('DebitAccount') || 'Debit')}
                                                        {renderSortHeader('creditAccount', t('CreditAccount') || 'Credit')}
                                                        {renderSortHeader('amount', t('Amount'), 'right')}
                                                        {renderSortHeader('vat', t('VAT') || 'VAT %', 'right')}
                                                        {renderSortHeader('sortCodeId', t('SortCode Id') || 'Sort Code Id')}
                                                        {renderSortHeader('sortCodeName', t('SortCodeName') || 'Sort Code')}
                                                        {renderSortHeader('classification', t('Classification') || 'סיווג')}
                                                        {renderSortHeader('description', t('Description'))}
                                                        {renderSortHeader('isValid', t('Status') || 'Status')}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {mdbPaginatedEntries.map((row, i) => (
                                                        <TableRow
                                                            key={row.lineNumber ?? i}
                                                            sx={{
                                                                bgcolor: row.isDuplicate ? '#fff3e0' : row.isValid ? 'transparent' : 'action.hover',
                                                                opacity: row.isDuplicate && mdbSkipDuplicates ? 0.5 : 1,
                                                                textDecoration: row.isDuplicate && mdbSkipDuplicates ? 'line-through' : 'none',
                                                            }}
                                                        >
                                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: 12 }}>{row.lineNumber}</TableCell>
                                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: 12, whiteSpace: 'nowrap' }}>{row.documentDate ?? '—'}</TableCell>
                                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: 12, whiteSpace: 'nowrap' }}>{mdbFormatMonth(row.registrationMonth)}</TableCell>
                                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: 12, whiteSpace: 'nowrap' }}>{mdbFormatMonth(row.reportingMonth)}</TableCell>
                                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: 12 }}>{row.reference ?? '—'}</TableCell>
                                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: 12 }}>
                                                                {row.debitAccount}{row.debitAccountName ? ` (${row.debitAccountName})` : ''}
                                                            </TableCell>
                                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: 12 }}>
                                                                {row.creditAccount}{row.creditAccountName ? ` (${row.creditAccountName})` : ''}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: 12 }}>{mdbFormatAmount(row.amount)}</TableCell>
                                                            <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: 12 }}>
                                                                {row.vat != null ? `${Number(row.vat)}%` : '—'}
                                                            </TableCell>
                                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: 12 }}>{row.sortCodeId ?? '—'}</TableCell>
                                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: 12 }}>{row.sortCodeName ?? '—'}</TableCell>
                                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: 12 }}>{formatClassification(row.classification)}</TableCell>
                                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: 12, maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }} title={row.description}>
                                                                {row.description ?? ''}
                                                            </TableCell>
                                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: 12, maxWidth: 280, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                {row.isDuplicate ? (
                                                                    <Typography variant="body2" color="warning.main">{t('Duplicate') || 'Duplicate'}</Typography>
                                                                ) : row.isValid ? (
                                                                    <Typography variant="body2" color="success.main">{t('Valid') || 'OK'}</Typography>
                                                                ) : row.errorMessage === 'ExcludedByVatReport' ? (
                                                                    <Typography variant="body2" color="warning.dark">
                                                                        {t('ExcludedByVatReport') || 'Excluded (VAT report)'}
                                                                    </Typography>
                                                                ) : (
                                                                    <Typography variant="body2" color="error.main" title={row.errorMessage}>{row.errorMessage ?? ''}</Typography>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                            <TablePagination
                                                component="div"
                                                count={mdbFilteredEntries.length}
                                                page={mdbPreviewPage}
                                                onPageChange={(_, p) => setMdbPreviewPage(p)}
                                                rowsPerPage={mdbPreviewRowsPerPage}
                                                onRowsPerPageChange={e => { setMdbPreviewRowsPerPage(parseInt(e.target.value, 10)); setMdbPreviewPage(0); }}
                                                rowsPerPageOptions={[10, 25, 50, 100]}
                                                labelRowsPerPage={t('RowsPerPage') || 'Rows per page'}
                                            />
                                        </TableContainer>
                                    )}
                                </Box>
                            )}
                        </>
                    )}
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default MdbImportModal;
